import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SESSION_TIMEOUT = 720 * 60 * 1000; // 12時間
const SESSION_KEY = 'sessionExpiry';
const USER_KEY = 'userData';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5分ごとにチェック
const TOKEN_KEY = 'token';

// localStorageの存在チェックと安全なアクセス関数
const isLocalStorageAvailable = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
};

const safeLocalStorageGet = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return null;
  } catch (error) {
    console.error('localStorage get error:', error);
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage set error:', error);
    return false;
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage remove error:', error);
    return false;
  }
};

/** localStorage からセッション有効性を同期的に判定 */
export function isSessionValid() {
  try {
    const expiry = safeLocalStorageGet(SESSION_KEY);
    const userData = safeLocalStorageGet(USER_KEY);
    const token = safeLocalStorageGet(TOKEN_KEY);

    if (!token || !expiry || !userData) {
      return false;
    }

    return new Date().getTime() <= parseInt(expiry, 10);
  } catch {
    return false;
  }
}

let isRedirectingToLogin = false;
let isAuthFetchInterceptorInstalled = false;

function redirectToLogin() {
  if (window.location.pathname === '/login') {
    isRedirectingToLogin = false;
    return;
  }
  window.location.replace('/login');
}

/** セッション情報を消してログイン画面へ遷移する（fetch インターセプタからも利用） */
export function clearSessionAndRedirectToLogin() {
  if (isRedirectingToLogin) {
    return;
  }
  isRedirectingToLogin = true;
  try {
    safeLocalStorageRemove(SESSION_KEY);
    safeLocalStorageRemove(USER_KEY);
    safeLocalStorageRemove(TOKEN_KEY);
  } catch (error) {
    console.error('Session clear error:', error);
  }
  redirectToLogin();
}

function getFetchUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input === 'object' && 'url' in input) return input.url;
  return '';
}

/** ログイン API はパスワード誤り等の 401 があるため対象外 */
function isLoginApiUrl(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path === '/api3000/login' || path === '/api3000/google-login';
  } catch {
    return String(url).includes('/api3000/login') || String(url).includes('/api3000/google-login');
  }
}

/** カレンダーAPIの 401/403 はセッション切れではない。画面側でエラー表示する */
function isGoogleCalendarApiUrl(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.startsWith('/api3010/');
  } catch {
    return String(url).includes('/api3010/');
  }
}

/**
 * JWT 切れ・token なしの認証エラーか。
 * authenticateToken の sendStatus(401/403) は "Unauthorized" / "Forbidden" のテキスト。
 * 業務の 403 は JSON ({ error: 'Not allowed ...' }) なので除外する。
 */
async function isJwtAuthError(res) {
  if (res.status === 401) return true;
  if (res.status !== 403) return false;
  const contentType = res.headers.get('content-type') || '';
  try {
    const text = (await res.clone().text()).trim();
    if (!text) return true;
    if (contentType.includes('application/json')) return false;
    return text === 'Forbidden' || text === '403';
  } catch {
    return true;
  }
}

/** 全 fetch で JWT 切れを検知しログインへ戻す */
export function installAuthFetchInterceptor() {
  if (typeof window === 'undefined' || isAuthFetchInterceptorInstalled) {
    return;
  }
  isAuthFetchInterceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await originalFetch(input, init);
    const url = getFetchUrl(input);
    if (isLoginApiUrl(url) || isGoogleCalendarApiUrl(url)) {
      return res;
    }
    if (await isJwtAuthError(res)) {
      clearSessionAndRedirectToLogin();
    }
    return res;
  };
}

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(isSessionValid);

  const extendSession = useCallback(() => {
    try {
      const newExpiry = new Date().getTime() + SESSION_TIMEOUT;
      safeLocalStorageSet(SESSION_KEY, newExpiry.toString());
    } catch (error) {
      console.error('Session extension error:', error);
    }
  }, []);

  const startSession = useCallback((userData) => {
    try {
      const newExpiry = new Date().getTime() + SESSION_TIMEOUT;
      const success1 = safeLocalStorageSet(SESSION_KEY, newExpiry.toString());
      const success2 = safeLocalStorageSet(USER_KEY, JSON.stringify(userData));

      if (success1 && success2) {
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session start error:', error);
      return false;
    }
  }, []);

  const endSession = useCallback(() => {
    setIsAuthenticated(false);
    clearSessionAndRedirectToLogin();
  }, []);

  // セッション監視
  useEffect(() => {
    const checkSession = () => {
      const authenticated = isSessionValid();
      if (!authenticated && isAuthenticated) {
        setIsAuthenticated(false);
        clearSessionAndRedirectToLogin();
      }
    };

    const interval = setInterval(checkSession, CHECK_INTERVAL);

    const handleUserActivity = () => {
      if (isAuthenticated) {
        extendSession();
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isAuthenticated, extendSession]);

  const value = {
    isAuthenticated,
    startSession,
    extendSession,
    endSession,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
